import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { HighlightText } from '@/components/common/HighlightText';
import { RowEditDeleteButtons } from '@/components/common/RowActions';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { STATUS_TEXT_CLASS } from '@/styles/statusColors';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type { BookEdition, BookType, Specimen } from '../types/product.types';

export interface RowActions<TRow> {
  onEdit: (row: TRow) => void;
  onDelete?: (row: TRow) => void;
}

const dash = (value: string | null): string =>
  value === null || value === '' ? EMPTY_VALUE : value;

function highlightWhenPresent(text: string | null, searchText: string | undefined) {
  if (text === null || text === '') return null;
  return <HighlightText text={text} highlightedText={searchText} />;
}

function nameWithInactiveNote(name: string | null, isInactive: boolean) {
  return (
    <StackedCellText
      primary={dash(name)}
      secondary={isInactive ? <span className={STATUS_TEXT_CLASS.danger}>Inactive</span> : null}
    />
  );
}

function actionsColumn<TRow extends { name: string }>(
  actions: RowActions<TRow>,
): ColumnDef<TRow, unknown> {
  return createActionsColumn<TRow>((item) => (
    <RowEditDeleteButtons
      item={item}
      itemName={item.name}
      onEdit={actions.onEdit}
      onDelete={actions.onDelete}
    />
  ));
}

export function getBookTypeColumns(
  searchText?: string,
  actions?: RowActions<BookType>,
): ColumnDef<BookType, unknown>[] {
  const columns: ColumnDef<BookType, unknown>[] = [
    createSerialColumn<BookType>(),
    {
      accessorKey: 'name',
      header: 'Book Type',
      cell: ({ row }) => (
        <span className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </span>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <WrappingCellText width="wide">{dash(row.original.description)}</WrappingCellText>
      ),
    },
    {
      accessorKey: 'groupNo',
      header: 'Group',
      cell: ({ row }) => row.original.groupNo ?? '—',
    },
    {
      accessorKey: 'specimenCount',
      header: 'Specimens',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.specimenCount)}</span>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
  ];

  if (actions) columns.push(actionsColumn(actions));
  return columns;
}

export function getBookEditionColumns(
  searchText?: string,
  actions?: RowActions<BookEdition>,
): ColumnDef<BookEdition, unknown>[] {
  const columns: ColumnDef<BookEdition, unknown>[] = [
    createSerialColumn<BookEdition>(),
    {
      accessorKey: 'name',
      header: 'Edition',
      cell: ({ row }) => (
        <StackedCellText
          primary={<HighlightText text={row.original.name} highlightedText={searchText} />}
          primaryClassName="font-medium"
          secondary={row.original.nameLocal}
        />
      ),
    },
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => <span className="text-xs tabular-nums">{dash(row.original.code)}</span>,
    },
    {
      accessorKey: 'specimenCount',
      header: 'Specimens',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.specimenCount)}</span>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
  ];

  if (actions) columns.push(actionsColumn(actions));
  return columns;
}

export function getSpecimenColumns(
  searchText?: string,
  actions?: RowActions<Specimen>,
): ColumnDef<Specimen, unknown>[] {
  const columns: ColumnDef<Specimen, unknown>[] = [
    createSerialColumn<Specimen>(),
    {
      accessorKey: 'name',
      header: 'Specimen Name',
      cell: ({ row }) => (
        <StackedCellText
          primary={<HighlightText text={row.original.name} highlightedText={searchText} />}
          primaryClassName="font-medium"
          secondary={highlightWhenPresent(row.original.nameLocal, searchText)}
        />
      ),
    },
    {
      accessorKey: 'spCode',
      header: 'SP Code',
      cell: ({ row }) => (
        <span className="text-xs tabular-nums">
          <HighlightText text={dash(row.original.spCode)} highlightedText={searchText} />
        </span>
      ),
    },
    {
      accessorKey: 'misCode',
      header: 'MIS Code / Bar Code',
      cell: ({ row }) => (
        <StackedCellText
          primary={<HighlightText text={dash(row.original.misCode)} highlightedText={searchText} />}
          primaryClassName="text-xs tabular-nums"
          secondary={highlightWhenPresent(row.original.barCode, searchText)}
        />
      ),
    },
    {
      accessorKey: 'bookTypeName',
      header: 'Book Type',
      cell: ({ row }) =>
        nameWithInactiveNote(row.original.bookTypeName, row.original.hasInactiveBookType),
    },
    {
      accessorKey: 'editionName',
      header: 'Edition',
      cell: ({ row }) =>
        nameWithInactiveNote(row.original.editionName, row.original.hasInactiveEdition),
    },
    {
      accessorKey: 'insertedAt',
      header: 'Inserted',
      cell: ({ row }) => (
        <span className="whitespace-nowrap">{formatDisplayDateTime(row.original.insertedAt)}</span>
      ),
    },
    {
      accessorKey: 'activeFrom',
      header: 'Active From / To',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDisplayDate(row.original.activeFrom)}
          secondary={formatDisplayDate(row.original.activeTo)}
        />
      ),
    },
    {
      accessorKey: 'netPrice',
      header: 'Net Price',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.netPrice ?? 0)}</span>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
  ];

  if (actions) columns.push(actionsColumn(actions));
  return columns;
}
